"use client";

import dynamic from "next/dynamic";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Compass, ChevronUp, X, Brain } from "lucide-react";
import { eventBus } from "@/lib/utils/eventBus";
import type { AiProfile } from "@/types/ai";
import type { V11StreamLine } from "@/lib/mindaloo/useKarmaV11Stream";
import type {
  AtlasNavigationResult,
  AtlasSearchProcess,
} from "@/lib/atlas/integration";
import { getAtlasStatusText } from "@/lib/atlas/getAtlasStatusText";
import { useAtlasNavExpansion } from "./useAtlasNavExpansion";
import VoiceListeningIndicator from "@/components/mindaloo/VoiceListeningIndicator";

const AtlasFullscreenView = dynamic(
  () => import("@/components/chat/UltraNavAtlasFullscreen"),
  { ssr: false }
);

interface ActiveTileInfo {
  id: string;
  name: string;
  icon?: string;
  type: string;
  profile: AiProfile | null;
  minimized?: boolean;
}

export interface AtlasNavChromeProps {
  profile: AiProfile;
  atlasProcess: AtlasSearchProcess | null;
  atlasNavigationResult: AtlasNavigationResult | null;
  atlasHistory: Array<{
    process: AtlasSearchProcess;
    result: AtlasNavigationResult;
    messageId: string;
    timestamp: number;
  }>;
  primaryColor: string;
  secondaryColor: string;
  barTextColor: string;
  overlayClassName?: string;
  children?: React.ReactNode;
  activeTiles?: ActiveTileInfo[];
  v11Lines?: V11StreamLine[];
  v11Live?: boolean;
  voiceListening?: boolean;
  voiceSession?: Array<{ role: "user" | "assistant"; content: string }>;
  onOutcome?: (outcome: "accepted" | "corrected" | "rejected") => void;
}

/** Typewriter hook — cycles through target string char by char */
function useTypewriter(target: string, speed = 28) {
  const [displayed, setDisplayed] = useState("");
  const prevRef = useRef("");
  useEffect(() => {
    if (target === prevRef.current) return;
    prevRef.current = target;
    setDisplayed("");
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayed(target.slice(0, i));
      if (i >= target.length) clearInterval(iv);
    }, speed);
    return () => clearInterval(iv);
  }, [target, speed]);
  return displayed;
}

function VoiceSessionStrip({
  voiceSession,
  primaryColor,
  barTextColor,
}: {
  voiceSession: Array<{ role: "user" | "assistant"; content: string }>;
  primaryColor: string;
  barTextColor: string;
}) {
  const latest = voiceSession[voiceSession.length - 1];
  if (!latest) return null;
  const label = latest.role === "user" ? "You" : "Karma";
  const text = latest.content.length > 90 ? latest.content.slice(0, 90) + "…" : latest.content;
  return (
    <>
      <span className="w-2 h-2 rounded-full shrink-0 animate-pulse" style={{ background: latest.role === "user" ? "#a78bfa" : primaryColor }} />
      <span className="text-[11px] font-medium shrink-0" style={{ color: primaryColor }}>{label}</span>
      <span className="text-xs font-medium truncate flex-1 text-left font-mono" style={{ color: barTextColor }}>{text}</span>
      <span className="text-[10px] opacity-50 shrink-0" style={{ color: barTextColor }}>{voiceSession.length}</span>
    </>
  );
}

/**
 * Atlas strip above command bar.
 * State 0 → compact pill strip
 * State 1 → semi-expanded peek card (scrollable summary)
 * State 2 → fullscreen overlay
 */
export default function AtlasNavChrome({
  profile,
  atlasProcess,
  atlasNavigationResult,
  atlasHistory,
  primaryColor,
  secondaryColor,
  barTextColor,
  overlayClassName = "!z-[10250]",
  activeTiles = [],
  v11Lines = [],
  v11Live = false,
  voiceListening = false,
  voiceSession = [],
  onOutcome,
}: AtlasNavChromeProps) {
  const streamBottomRef = useRef<HTMLDivElement>(null);
  const { atlasExpansionState, setAtlasExpansionState, atlasContainerRef } =
    useAtlasNavExpansion(atlasProcess);
  const [activeTab, setActiveTab] = useState<"consciousness" | "overview" | string>("consciousness");

  const selectedTile = activeTab !== "overview" && activeTab !== "consciousness"
    ? activeTiles.find((t) => t.id === activeTab) ?? null
    : null;

  const tabProfile = selectedTile?.profile ?? profile;
  const tabPrimary = tabProfile?.brand?.primaryColor ?? primaryColor;

  const latestThought = v11Lines.filter((l) => l.type === "thought").slice(-1)[0]?.text ?? "";
  const latestOutput = v11Lines.filter((l) => l.type === "output").slice(-1)[0]?.text ?? "";
  const pillTarget = latestThought || latestOutput || getAtlasStatusText(atlasProcess ?? null, atlasNavigationResult);
  const pillText = useTypewriter(pillTarget, 22);

  useEffect(() => {
    streamBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [voiceSession.length]);

  if (!atlasProcess && v11Lines.length === 0 && activeTiles.length === 0 && voiceSession.length === 0) return null;

  const statusText = atlasProcess ? getAtlasStatusText(atlasProcess, atlasNavigationResult) : "Karma Core";
  const location = atlasNavigationResult?.location?.topicName;
  const summary = atlasNavigationResult?.orientation?.message ?? atlasProcess?.message ?? statusText;
  const historyItems = atlasHistory.slice(-4).reverse();

  return (
    <>
      <AnimatePresence mode="wait">
        {atlasExpansionState === 0 && (
          /* Compact pill strip */
          <motion.div
            key="strip"
            ref={atlasContainerRef}
            className="pointer-events-auto mb-1.5 flex flex-col gap-1"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.2 }}
          >
            {/* Atlas status row — atlas, voice, or v11 */}
            {(atlasProcess || v11Lines.length > 0 || voiceListening || voiceSession.length > 0) && (
              <button
                type="button"
                className="atlas-strip w-full"
                style={{
                  background: voiceListening
                    ? `linear-gradient(90deg, ${primaryColor}28 0%, ${secondaryColor}35 50%, ${primaryColor}28 100%)`
                    : voiceSession.length > 0
                    ? `linear-gradient(90deg, ${primaryColor}18 0%, ${secondaryColor}22 50%, ${primaryColor}18 100%)`
                    : `linear-gradient(90deg, ${primaryColor}12 0%, ${secondaryColor}15 50%, ${primaryColor}12 100%)`,
                  border: `1px solid ${voiceListening ? primaryColor : primaryColor + "25"}`,
                  boxShadow: voiceListening
                    ? `0 0 28px ${primaryColor}55, inset 0 1px 0 rgba(255,255,255,0.2)`
                    : `0 2px 12px ${primaryColor}20, inset 0 1px 0 rgba(255,255,255,0.15)`,
                  transition: "all 0.3s ease",
                }}
                onClick={() => setAtlasExpansionState(1)}
                aria-label="Expand Atlas"
              >
                {voiceListening ? (
                  <VoiceListeningIndicator color={primaryColor} />
                ) : voiceSession.length > 0 ? (
                  <VoiceSessionStrip voiceSession={voiceSession} primaryColor={primaryColor} barTextColor={barTextColor} />
                ) : (
                  <>
                    <Compass className="w-3 h-3 shrink-0" style={{ color: primaryColor }} />
                    <span className="text-xs font-medium truncate flex-1 text-left font-mono" style={{ color: barTextColor }}>
                      {pillText || statusText}
                      {pillText.length < pillTarget.length && (
                        <span className="inline-block w-0.5 h-3 ml-0.5 align-middle animate-pulse" style={{ background: primaryColor }} />
                      )}
                    </span>
                  </>
                )}
                <ChevronUp className="w-3 h-3 shrink-0 opacity-50" style={{ color: barTextColor }} />
              </button>
            )}

            {/* The open-tiles list used to live here as a second collapsible
                row. It's now part of the top consciousness bar instead (see
                KarmaDynamicIsland.tsx) — one adaptive bar tracks both which
                tile is active and which tiles exist, rather than splitting
                that across a top bar and a bottom strip. */}
          </motion.div>
        )}

        {atlasExpansionState === 1 && (
          /* Semi-expanded peek card */
          <motion.div
            key="peek"
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 420, damping: 36 }}
            className="atlas-peek karma-glass karma-neon-ring mb-2 pointer-events-auto"
            style={{ borderColor: `${tabPrimary}30` }}
          >
            {/* ── Tab bar — always visible ── */}
            <div className="flex items-center gap-0.5 px-2 pt-2 pb-1 overflow-x-auto scrollbar-hide shrink-0">
              {/* Consciousness tab — always first */}
              <button
                type="button"
                onClick={() => setActiveTab("consciousness")}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium shrink-0 transition"
                style={{
                  background: activeTab === "consciousness" ? `${primaryColor}25` : "transparent",
                  color: activeTab === "consciousness" ? primaryColor : "rgba(255,255,255,0.4)",
                  border: activeTab === "consciousness" ? `1px solid ${primaryColor}35` : "1px solid transparent",
                }}
              >
                <Brain className="w-2.5 h-2.5 shrink-0" />
                <span>Core</span>
                {v11Live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />}
              </button>
              {/* Atlas overview tab */}
              <button
                type="button"
                onClick={() => setActiveTab("overview")}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium shrink-0 transition"
                style={{
                  background: activeTab === "overview" ? `${primaryColor}25` : "transparent",
                  color: activeTab === "overview" ? primaryColor : "rgba(255,255,255,0.4)",
                  border: activeTab === "overview" ? `1px solid ${primaryColor}35` : "1px solid transparent",
                }}
              >
                <Compass className="w-2.5 h-2.5 shrink-0" />
                Atlas
              </button>
              {/* Per-tile tabs */}
              {activeTiles.map((t) => {
                const tc = t.profile?.brand?.primaryColor ?? primaryColor;
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium shrink-0 transition max-w-[96px]"
                    style={{
                      background: isActive ? `${tc}25` : "transparent",
                      color: isActive ? tc : "rgba(255,255,255,0.4)",
                      border: isActive ? `1px solid ${tc}35` : "1px solid transparent",
                    }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tc }} />
                    <span className="truncate">{t.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Peek header */}
            <div className="atlas-peek-header">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Compass className="w-3.5 h-3.5 shrink-0" style={{ color: tabPrimary }} />
                <span className="text-xs font-semibold truncate" style={{ color: tabPrimary }}>
                  {selectedTile ? selectedTile.name : (location ?? "Atlas Navigation")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="atlas-peek-btn"
                  onClick={(e) => { e.stopPropagation(); setAtlasExpansionState(2); }}
                  title="Full view"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  className="atlas-peek-btn"
                  onClick={(e) => { e.stopPropagation(); setAtlasExpansionState(0); }}
                  title="Close"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Scrollable body — switches by active tab */}
            <div className="atlas-peek-body scrollbar-hide">
              {activeTab === "consciousness" ? (
                /* ── Karma <-> you, the actual conversation, not V11's internal
                    narration — V11 isn't a live backend yet, so that stream
                    was always either empty or the same "offline / fallback"
                    line repeated on every poll. This is what people actually
                    want to see when they open this tab. ── */
                <div className="space-y-2 text-[11px]">
                  {voiceSession.length === 0 ? (
                    <p className="text-[11px] opacity-30 italic">No conversation yet — say something to Karma.</p>
                  ) : (
                    voiceSession.map((turn, i) => (
                      <div key={i} className="leading-relaxed mindaloo-fade-in">
                        <span
                          className="text-[9px] uppercase tracking-widest opacity-40 mr-1.5"
                          style={{ color: turn.role === "user" ? "rgba(255,255,255,0.4)" : primaryColor }}
                        >
                          {turn.role === "user" ? "You" : "Karma"}
                        </span>
                        <span style={{ color: turn.role === "user" ? "rgba(255,255,255,0.7)" : "#e2e8f0" }}>
                          {turn.content}
                        </span>
                      </div>
                    ))
                  )}
                  <div ref={streamBottomRef} />
                </div>
              ) : selectedTile ? (
                /* ── Per-tile view ── */
                <>
                  <div className="flex items-center gap-2 mb-2 px-0.5">
                    <span className="text-[9px] uppercase tracking-widest opacity-40">Tile · {selectedTile.type}</span>
                    <span className="w-1 h-1 rounded-full opacity-30" style={{ background: tabPrimary }} />
                    <span className="text-[9px] opacity-40 truncate">{selectedTile.name}</span>
                  </div>
                  {summary && <p className="text-xs leading-relaxed opacity-80 mb-2">{summary}</p>}
                </>
              ) : (
                /* ── Atlas overview ── */
                <>
                  {summary && (
                    <p className="text-xs leading-relaxed opacity-80 mb-2">{summary}</p>
                  )}
                  {historyItems.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-[10px] uppercase tracking-widest opacity-40 mb-1">Recent navigations</p>
                      {historyItems.map((h, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11px] opacity-60">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: primaryColor }} />
                          <span className="truncate">{h.result?.location?.topicName ?? h.process.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {/* The fullscreen expansion below is Atlas-navigation-specific
                  (it requires atlasProcess and renders Atlas's own status/
                  history UI) — it was never actually built for the Core tab's
                  content, so "Open Consciousness view" led nowhere new when
                  atlasProcess was empty, or opened Atlas's view mislabeled as
                  Karma's when it wasn't. The scrollable conversation above is
                  the real view for now; no separate fullscreen for it yet. */}
              {activeTab !== "consciousness" && (
                <button
                  type="button"
                  className="atlas-peek-full-btn mt-3 w-full"
                  style={{ color: tabPrimary, borderColor: `${tabPrimary}30` }}
                  onClick={(e) => { e.stopPropagation(); setAtlasExpansionState(2); }}
                >
                  {selectedTile ? `Open ${selectedTile.name} Atlas` : "Open full Atlas view"}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {atlasExpansionState === 2 && atlasProcess && (
          <AtlasFullscreenView
            profile={tabProfile}
            atlasProcess={atlasProcess}
            atlasNavigationResult={atlasNavigationResult}
            atlasHistory={atlasHistory}
            primaryColor={tabPrimary}
            secondaryColor={tabProfile?.brand?.secondaryColor ?? secondaryColor}
            onClose={() => setAtlasExpansionState(0)}
            onOutcome={onOutcome}
            overlayClassName={overlayClassName}
          />
        )}
      </AnimatePresence>
    </>
  );
}
